<template>
    <div class="modal fade" :id="id" aria-labelledby="system-modal" aria-hidden="true" data-bs-backdrop="static">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
           <div class="modal-content">
                <div class="modal-header border-bottom-0">
                    <div class="w-100 d-flex flex-column gap-1">
                        <h2 class="mb-0 mt-2">Add a System</h2>
                    </div>
                    <button type="button" class="btn btn-white float-end close-modal"
                        :data-bs-dismiss="'#'+id" @click.prevent="closeModal()" data-bs-toggle="tooltip" title="Close">
                        <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt=""> 
                    </button>
                </div>
                <div class="modal-body pt-0" v-if="!selected_integration?.id">
                    <p class="mb-2">Connect a system to unleash more insights!</p>
                    
                    <!-- Integration Type Filters -->
                    <div class="integration-type-filters mb-3">
                        <div class="d-flex flex-wrap gap-2">
                            <button 
                                v-for="type in availableIntegrationTypes" 
                                :key="type"
                                @click="filterByType(type)"
                                class="btn btn-sm box-shadowed"
                                :class="selectedTypeFilter === type ? 'btn-primary' : 'btn-secondary'"
                            >
                                {{ formatIntegrationType(type) }}
                            </button>
                        </div>
                    </div>
                    
                    <div class="row">
                        <div class="col-12 position-relative mb-3">
                            <input type="text" class="search-box form-control me-1" placeholder="Search by Name..." aria-label="Search by Name..." v-model="search_key" @input="searchIntegration()">
                            <i class="fa fa-search search-placeholder-icon" v-if="!search_key"></i>
                        </div>
                    </div>
                    
                    <div class="d-flex justify-content-between align-items-center mb-2 integration-count-display">
                        <small class="text-muted">
                            Showing {{ filteredIntegrations.length }} of {{ local_integrations.length }} integrations
                        </small>
                        <button v-if="selectedTypeFilter !== 'all' || search_key" @click="clearFilters" class="btn btn-link btn-sm p-0">
                            Clear filters
                        </button>
                    </div>
                    
                    <div class="system-connections-grid-in-modal row">
                        <div v-if="filteredIntegrations.length === 0" class="col-12 text-center py-4">
                            <p class="text-muted mb-0">No integrations found matching your criteria.</p>
                        </div>
                        <div v-for="(integration, index) in filteredIntegrations" :key="integration.id" class="col-lg-6 col-12 mb-3 d-flex">
                            <div class="card system-connection-card w-100" 
                                :class="{'system-connection-card-selected': integration.id === selected_integration?.id}" 
                                @click.prevent="changeIntegrationSelection(integration)">
                                <div class="card-body system-connection-card__header">
                                    <div class="d-flex align-items-center">
                                        <img :src="integration.logo_url" :alt="integration.display_name" class="avatar rounded-circle avatar-sm me-2 bg-white">
                                        <h3 class="my-0">{{ integration.display_name }}</h3>                                        
                                    </div>
                                    <div class="connection-capabilities-badges mt-3 d-flex flex-wrap gap-2">
                                        <span class="badge capability-badge capability-badge-reporting">
                                            <img class="icon me-1" src="/img/icons/nav-icons/Insights.svg" alt="Reporting">
                                            Reporting
                                        </span>
                                        <span v-if="hasAudiencesCapability(integration)" class="badge capability-badge capability-badge-audiences">
                                            <img class="icon me-1" src="/img/icons/nav-icons/Pathways.svg" alt="Audiences">
                                            Audiences
                                        </span>
                                        <span v-if="hasPlaybooksCapability(integration)" class="badge capability-badge capability-badge-playbooks">
                                            <img class="icon me-1" src="/img/icons/nav-icons/Playbooks.svg" alt="Playbooks">
                                            Playbooks
                                        </span>
                                    </div>     
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="modal-body pt-0" v-if="selected_integration?.id">
                    <div v-if="form.loading">
                        <div class="d-flex flex-column gap-2 align-items-center">
                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <h4 class="text-center">Loading System Information</h4>
                        </div>
                    </div>
                    <div v-if="!form.loading">
                        <button v-if="!local_connection_selected" class="btn btn-white back-button mb-2" @click.prevent="backToSelection()">
                            <img class="icon" src="/img/icons/avid-icons/chevrons-left.svg" alt="">
                            Back
                        </button>
                        <h2>
                            <img :src="selected_integration?.logo_url" :alt="selected_integration?.display_name" class="avatar rounded-circle avatar-sm me-2 bg-white">
                            {{ selected_integration?.display_name }}
                        </h2>
                        
                        <div class="alert alert-danger mt-3" v-show="form.error && !(selected_integration_info?.type === 'credentials' || selected_integration_info?.type === 'redirect')">
                            <span v-html="form.msg"></span>
                        </div>
                        
                        <div v-if="selected_integration_info?.type === 'credentials' || selected_integration_info?.type === 'redirect'">
                            <div v-html="selected_integration_info?.instructions || ''"></div>

                            <div class="system-connection-fields row mt-2">
                                <div v-for="(field, field_index) in (selected_integration_info?.field || [])" :key="field?.id" class="mb-1">
                                    <label v-if="field?.type !== 'checkbox' && field?.type !== 'hidden'" class="form-label">
                                        <span v-html="field?.label || ''"></span>
                                        <img v-if="field?.tooltip?.length" class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" data-bs-html="true" :title="field?.tooltip" alt=""></img>
                                    </label>

                                    <input v-if="field?.type === 'text'" 
                                        type="text" 
                                        class="form-control" 
                                        :class="{'is-invalid': field?.error}"
                                        :name="field?.id" 
                                        v-model="field.value"  
                                        :disabled="form.saving"
                                        @change="field.error = false" aria-label="Text input">

                                    <input v-if="field?.type === 'label'" 
                                        type="text"
                                        class="form-control" 
                                        disabled 
                                        :name="field?.id" 
                                        v-model="field.value" aria-label="Text input">

                                    <textarea v-if="field?.type === 'textarea'"  
                                        :disabled="form.saving" 
                                        :name="field?.id" 
                                        class="form-control full-height" 
                                        v-model="field.value"  
                                        @change="field.error = false">
                                    </textarea>

                                    <password-visibility
                                        v-if="field?.type === 'password' || field?.type === 'password-text'"
                                        :model-value="field.value"
                                        @update:modelValue="val => { field.value = val; field.error = false; }"
                                        :name="field?.id"
                                        :placeholder="field?.label || 'Password'"
                                        :required="field?.required || false"
                                        autocomplete="one-time-code"
                                        :has-error="field?.error"
                                        :show-inline-error="false"
                                        :disabled="form.saving"
                                        @change="field.error = false"
                                    />

                                    <div v-if="field?.type === 'select'">
                                        <select 
                                            class="form-select"  
                                            :disabled="form.saving" 
                                            :name="field?.id" 
                                            v-model="field.value" 
                                            :class="{'is-invalid': field?.error}"  
                                            @change="field.error = false" aria-label="Select option">
                                            <option v-for="option in Object.keys(field?.options || {})" :key="option" :value="option">
                                                {{field?.options?.[option]}}
                                            </option>
                                        </select>
                                    </div>

                                    <div v-if="field?.type === 'warning'">
                                        <p style="margin-top:-8px;"><small class="text-muted">{{ field?.text || '' }}</small></p>
                                        <div class="alert alert-warning" role="alert">
                                            <code v-html="field?.value || ''"></code>
                                        </div>
                                    </div>

                                    <div v-if="field?.type === 'vue-select'">
                                        <accordion-select  
                                            :disabled="form.saving" 
                                            :options="field?.options || []" 
                                            :name="field?.id" 
                                            v-model="field.value" 
                                            :class="{'is-invalid': field?.error}"  
                                            @change="field.error = false">
                                        </accordion-select>
                                    </div>

                                    <div v-if="field?.type === 'checkbox'" 
                                        class="form-check"  
                                        :class="{'is-invalid': field?.error}">
                                        <input 
                                            class="form-check-input"  
                                            :disabled="form.saving" 
                                            type="checkbox" 
                                            value="true" 
                                            :id="field?.id" 
                                            :name="field?.id" 
                                            v-model="field.value" aria-label="Checkbox">
                                        <label class="form-check-label" :for="field?.id">
                                            {{ field?.label }}
                                            <img v-if="field?.tooltip?.length" class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" :title="field?.tooltip" alt=""></img>
                                        </label>
                                    </div>

                                    <div v-if="field?.type === 'tables'" :id="field?.id">
                                        <div class="row">
                                            <div class="col-1"></div>
                                            <div class="col-sm-7">
                                                <label class="form-label">Dataset and Table Name    
                                                    <img class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" title="The value below should be in the format of [DATASET].[TABLE NAME]" alt=""></img>
                                                </label>
                                            </div>
                                            <div class="col">
                                                <label class="form-label">Table Type</label>
                                            </div>
                                            <div class="col-1"></div>
                                        </div>

                                        <div class="row mb-3" v-for="(row, row_index) in field.value" :key="row_index">
                                            <div class="col-1">
                                                <h3 class="text-end mb-0 mt-1 nums">{{ row_index + 1 }}.</h3>
                                            </div>
                                            <div class="col-sm-7">
                                                <input 
                                                    name="table_name[]"  
                                                    :disabled="form.saving" 
                                                    type="text" 
                                                    v-model="field.value[row_index]['table_name']" 
                                                    class="form-control"
                                                    :class="{'is-invalid': field.error && field.value[row_index]['table_name'] === ''}" aria-label="Table Name[]">
                                            </div>
                                            <div class="col">
                                                <select 
                                                    name="table_type[]" 
                                                    v-model="field.value[row_index]['table_type']"  
                                                    :disabled="form.saving"
                                                    class="form-select" 
                                                    v-html="bigquery_table_type_html" 
                                                    :class="{'is-invalid': field.error && field.value[row_index]['table_type'] === ''}"
                                                    @change="field.error = false" aria-label="Table Type[]">
                                                </select>
                                            </div>
                                            <div class="col-1 delete_table" v-if="field.value.length > 1">
                                                <button class="btn btn-none" @click.prevent="removeTable(field_index, row_index)">
                                                    <i class="fa fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="text-danger invalid-feedback" v-if="field.error">
                                            {{field.error_msg}}
                                        </div>

                                        <button class="btn btn-secondary mt-3 mb-4" @click.prevent="addTable(field_index)">
                                            <i class="fas fa-fw fa-plus"></i> Add Table
                                        </button>
                                    </div>

                                    <div class="text-danger invalid-feedback" v-if="field.error && field.type !== 'tables'">
                                        {{field.error_msg}}
                                    </div>
                                </div>
                            </div>

                            <div class="alert alert-danger mt-3" v-show="form.error">
                                <span v-html="form.msg"></span>
                            </div>

                            <div class="w-100 d-flex flex-row justify-content-center gap-3 mt-4">
                                <button class="btn btn-primary w-50" @click.prevent="addIntegration()" :disabled="form.saving">
                                    <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status" v-if="form.saving">
                                        <span class="visually-hidden"></span>
                                    </div>
                                    Save
                                </button>

                                <button class="btn btn-white w-50" @click.prevent="closeModal()" :disabled="form.saving">
                                    Cancel
                                </button>
                            </div>
                        </div>

                        <div v-if="selected_integration_info.type === 'oauth'" id="oauth-section">
                            <div v-if="selected_integration_info.instructions" v-html="selected_integration_info.instructions"></div>
                            <p v-else>
                                <strong>Avid connects to {{ selected_integration.display_name }} through a one-click authentication.</strong> 
                                To connect your instance, click on the button below&mdash;you will be redirecting to {{ selected_integration.display_name }} to complete the process. 
                                If additional details are needed after connecting, you'll be prompted when you get back.
                            </p>
                            
                            <p v-if="selected_integration.display_name === 'Salesforce'">
                                Need more information about how to connect your Salesforce instance? 
                                <a href='https://help.avidai.com/hc/en-us/articles/42928676503059-Salesforce-API-Only-Connection' target="_blank"> Check Avid's Knowledge Base.</a>
                            </p>
                            <p v-if="selected_integration.display_name === 'Pardot'">
                                Need more information about how to connect your Pardot instance? 
                                <a href='https://help.avidai.com/hc/en-us/articles/42928687755155-Connecting-Subscriber-Data-Pardot' target="_blank"> Check Avid's Knowledge Base.</a>
                            </p>
                            <p v-if="selected_integration.display_name === 'HubSpot'">
                                Need more information about how to connect your HubSpot instance? 
                                <a href='https://help.avidai.com/hc/en-us/articles/42928676727443-Connecting-Subscriber-Data-HubSpot' target="_blank"> Check Avid's Knowledge Base.</a>
                            </p>
                            <p v-if="selected_integration.display_name === 'Mailchimp'">
                                Need more information about how to connect your Mailchimp instance? 
                                <a href='https://help.avidai.com/hc/en-us/articles/42928661670035-Connecting-Subscriber-Data-Mailchimp' target="_blank"> Check Avid's Knowledge Base.</a>
                            </p>
                            <p v-if="selected_integration.display_name === 'Facebook'">
                                Need more information about how to connect your Facebook instance? 
                                <a href='https://help.avidai.com/hc/en-us/articles/42928630311699-Connecting-Advertising-Facebook' target="_blank"> Check Avid's Knowledge Base.</a>
                            </p>
                            <p v-if="selected_integration.display_name === 'Google Ads'">
                                Need more information about how to connect your Google Ads instance? 
                                <a href='https://help.avidai.com/hc/en-us/articles/42928646536211-Connecting-Advertising-Google-Ads' target="_blank"> Check Avid's Knowledge Base.</a>
                            </p>
                            
                            <div class="w-100 d-flex flex-row justify-content-center gap-3 mt-3">
                                <button class="btn btn-primary w-100" @click.prevent="redirectOauth(selected_integration_info.url)">
                                    <img class="icon p-0" src="/img/icons/avid-icons/profile-tick.svg" alt=""> Authenticate
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div> 
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import PasswordVisibility from '../PasswordVisibility.vue';

export default defineComponent({
    name: 'SystemModal',
    components: {
        Swal,
        PasswordVisibility
    },
    props: {
        client: {
            type: Object,
            required: true
        },
        integrations: {
            type: Array,
            required: true
        },
        connections: {
            type: Array,
            required: true
        },
        connection_selected: {
            type: Object,
            default: null
        },
        id: {
            type: String,
            default: 'system-modal'
        }
    },
    data() {
        return {
            integration_tab_list: ['all'],
            local_integrations: [],
            selected_integration: null,
            selected_integration_info: {
                type: null,
                field: [],
                instructions: '',
                url: '',
                id: null
            },
            bigquery_table_type_html: '',
            local_connection_selected: null,
            search_key: '',
            form: {
                saving: false,
                error: false,
                msg: "",
                loading: false
            },
            errors: {
                name: "",
                value: ""
            },
            availableIntegrationTypes: ['all', 'crm', 'email', 'advertising', 'email_form', 'analytics', 'donation_form', 'other'],
            selectedTypeFilter: 'all'
        };
    },
    computed: {
        filteredIntegrations() {
            let filtered = this.local_integrations;
            
            // Apply type filter
            if (this.selectedTypeFilter !== 'all') {
                filtered = filtered.filter(integration => 
                    integration.integration_types && 
                    integration.integration_types.includes(this.selectedTypeFilter)
                );
            }
            
            // Apply search filter
            if (this.search_key) {
                filtered = filtered.filter(integration => 
                    integration.display_name.toLowerCase().includes(this.search_key.toLowerCase())
                );
            }
            
            return filtered;
        }
    },
    beforeMount() { 
        // remove file from the integration list
        this.local_integrations = this.integrations.filter(integration => 
            integration.id !== '71b0c3b0-8390-49ec-9984-1c04b7384d38'
        );
        this.local_connection_selected = this.connection_selected;
    },
    watch: {
        connection_selected: {
            handler(newValue) {
                this.local_connection_selected = this.connection_selected;
                this.selected_integration = null;
                
                // Reset filters when modal is opened
                if (!newValue) {
                    this.resetFilters();
                }

                if (this.connection_selected && this.connection_selected.integration) {
                    this.selected_integration = this.connection_selected.integration;
                    this.fetchIntegrationDetailsWithValues(this.connection_selected);
                }
            },
            immediate: true
        },
        integrations: {
            handler(newValue) {
                this.local_connection_selected = this.connection_selected;
                this.local_integrations = this.integrations.filter(integration => 
                    integration.id !== '71b0c3b0-8390-49ec-9984-1c04b7384d38'
                );
            },
            immediate: true
        }
    },
    mounted() {
        this.initializeTooltips();
        this.resetFilters();
    },
    methods: {
        resetFilters() {
            this.selectedTypeFilter = 'all';
            this.search_key = '';
        },
        initializeTooltips() {
            // Initialize tooltips
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            // Add event listeners to hide tooltips on click and mouseleave
            tooltipTriggerList.forEach((trigger, index) => {
                trigger.addEventListener('click', () => {
                    tooltipList[index].hide();
                });
                trigger.addEventListener('mouseleave', () => {
                    tooltipList[index].hide();
                });
            });
        },
        redirectOauth(url) {
            let oauth_url = url;
            if (url.includes('?')) {
                oauth_url += '&';
            } else {
                oauth_url += '?';
            }

            const checkboxes = document.querySelectorAll('#oauth-section input[type="checkbox"]');
            checkboxes.forEach(checkbox => {
                if (checkbox.checked) {
                    oauth_url += `${checkbox.id}=true&`;
                }
            });

            if (oauth_url.endsWith('&')) {
                oauth_url = oauth_url.slice(0, -1);
            }

            window.location.href = oauth_url;
        },
        searchIntegration() {
            // This method is no longer needed as filtering is handled by computed properties
            // Keeping for backward compatibility but it's not used
        },
        backToSelection() {
            this.selected_integration = {};
            this.selected_integration_info = {};
        },
        changeIntegrationSelection(integration) {
            this.selected_integration = integration;
            this.form.loading = true;
            this.selected_integration_info = {
                type: null,
                field: [],
                instructions: '',
                url: '',
                id: null
            };

            if (integration.id === '66841ef7-ea17-4628-9b91-b6331523cd7e') {
                window.axios.get('/api/integration/data_source/data_set')
                    .then(response => {
                        if (response.status === 200) {
                            this.bigquery_table_type_html = response.data.html;
                        }
                    });
            }
            let params = integration.id;
            // Avid SFTP or Forwarded Email
            if (integration.id === '7dbce608-4906-4606-977c-32ab1259b2e6' || integration.id === '323ce508-d21b-44ec-8064-5623e498ad13') { 
                let url = (integration.id === '7dbce608-4906-4606-977c-32ab1259b2e6') ? 'sftp' : 'forward_email';
                window.axios.post('/api/integrations/' + url + '/setup', {
                    client_id: this.client.id
                })
                .then(response => {
                    this.local_connection_selected = response.data.connection;
                    this.getIntegrationFields(params + '/' + this.local_connection_selected.id);
                })
            } else {
                this.getIntegrationFields(params);
            }

        },
        getIntegrationFields(integration_id) {
            window.axios.get('/api/integration/' + integration_id)
                .then(response => {
                    if (response.data) {
                        this.selected_integration_info = {
                            ...this.selected_integration_info,
                            ...response.data
                        };
                        if (response.data.type === 'credentials' || response.data.type === 'redirect') {
                            const fields = response.data.field || [];
                            fields.forEach(field => {
                                if (field.type === 'select' && field.options) {
                                    field.value = Object.keys(field.options)[0];
                                }
                                field.error = false;
                            });
                        }
                    }
                    this.form.loading = false;
                    // Reinitialize tooltips after content changes
                    this.$nextTick(() => {
                        this.initializeTooltips();
                    });
                })
                .catch(error => {
                    this.form.loading = false;
                    this.form.error = true;
                    this.form.msg = error.response?.data?.message || 'An error occurred while fetching integration details';
                });
        },
        fetchIntegrationDetailsWithValues(connection) {
            this.form.loading = true;
            if (connection.integration.id === '66841ef7-ea17-4628-9b91-b6331523cd7e') {
                window.axios.get('/api/integration/data_source/data_set')
                    .then(response => {
                        this.bigquery_table_type_html = response.data.html;
                    });
            }

            window.axios.get('/api/integration/' + connection.integration.id + '/' + connection.id)
                .then(response => {
                    this.selected_integration_info = response.data;
                    if (response.data.type === 'credentials') {
                        const fields = response.data.field;
                        fields.forEach(field => {
                            field.error = false;
                        });
                    }
                    this.form.loading = false;
                    // Reinitialize tooltips after content changes
                    this.$nextTick(() => {
                        this.initializeTooltips();
                    });
                })
                .catch(error => {
                    this.form.loading = false;
                    this.form.error = true;
                    this.form.msg = error.response.data.message;
                });
        },
        addTable(field_index) {
            this.selected_integration_info.field[field_index].value.push({
                table_name: "",
                table_type: ""
            });
        },
        removeTable(field_index, table_row_index) {
            this.selected_integration_info.field[field_index].value.splice(table_row_index, 1);
        },
        isFormValid() {
            if (!this.selected_integration_info?.field) {
                return true;
            }

            let is_valid = true;
            this.selected_integration_info.field.forEach(field => {
                if (field.type === 'tables') {
                    field.value.forEach(row => {
                        if (!row.table_name || !row.table_type) {
                            field.error = true;
                            field.error_msg = 'This field is required.';
                            if (field.value.length > 1) {
                                field.error_msg += ' Be sure to remove any empty rows.';
                            }
                            is_valid = false;
                        } else {
                            field.error = false;
                        }
                    });
                } else if (field.required && !field.value) {
                    field.error = true;
                    field.error_msg = 'This field is required';
                    is_valid = false;
                } else {
                    field.error = false;
                }
            });

            return is_valid;
        },
        addIntegration() {
            if (!this.isFormValid()) {
                this.form.msg = "There was an error saving your information.";
                this.form.error = true;
                return;
            }

            this.form.msg = "";
            this.form.error = false;
            this.form.saving = true;
            
            const data = {
                client_id: this.client.id,
                ...(this.local_connection_selected && { conn_id: this.local_connection_selected.id })
            };

            this.selected_integration_info.field.forEach(field => {
                data[field.id] = field.value;
            });
        
            if (this.selected_integration_info.type === 'redirect') {
                const form = document.createElement('form');
                form.method = 'POST';
                form.action = this.selected_integration_info.url;

                Object.entries(data).forEach(([key, value]) => {
                    const input = document.createElement('input');
                    input.type = 'hidden';
                    input.name = key;
                    input.value = value;
                    form.appendChild(input);
                });

                document.body.appendChild(form);
                form.submit();
                return;
            }
        
            window.axios.post('/api' + this.selected_integration_info.url, data)
                .then(response => {
                    this.form.saving = false;
                    this.closeModal();
                    this.$emit('connectionAdded', response.data.connection);
                })
                .catch(error => {
                    this.form.msg = error.response.data.message || "There was an error saving your information. Please try again shortly";
                    this.form.saving = false;
                    this.form.error = true;
                });
        },
        closeModal() {
            const myModalEl = document.getElementById(this.id);
            const modal = bootstrap.Modal.getInstance(myModalEl);
            modal.hide();
            this.$emit('resetModal');

            myModalEl.addEventListener('hidden.bs.modal', () => {
                const backdrop = document.querySelector('.modal-backdrop');
                if (backdrop) {
                    backdrop.parentNode.removeChild(backdrop);
                }
            }, { once: true });

            this.form.saving = false;
            this.form.loading = false;
            this.form.error = false;
            this.selected_integration = {};
            this.selected_integration_info = {};
            this.selectedTypeFilter = 'all';
            this.search_key = '';
        },
        filterByType(type) {
            this.selectedTypeFilter = type;
        },
        clearFilters() {
            this.selectedTypeFilter = 'all';
            this.search_key = '';
        },
        setPreselectedType(type) {
            if (type && this.availableIntegrationTypes.includes(type)) {
                this.selectedTypeFilter = type;
            }
        },
        formatIntegrationType(type) {
            switch (type) {
                case 'crm':
                    return 'CRM';
                case 'email':
                    return 'Email Marketing';
                case 'advertising':
                    return 'Advertising';
                case 'email_form':
                    return 'Email Capture';
                case 'analytics':
                    return 'Analytics';
                case 'donation_form':
                    return 'Donation Forms';
                case 'other':
                    return 'Other';
                default:
                    return 'All';
            }
        },
        hasAudiencesCapability(integration) {
            // Audiences: has a value in census_connection_type column
            return integration.census_connection_type && integration.census_connection_type.trim() !== '';
        },
        hasPlaybooksCapability(integration) {
            // Playbooks: playbook_deployment is true
            return integration.playbook_deployment === true || integration.playbook_deployment === 1;
        }
    }
});
</script>
